import { getCookie, loadScript } from '/src/utils';
import { COOKIE_CONSENT } from '/src/consts';

const GRANTED = 'granted';
const DENIED = 'denied';

window.dataLayer = window.dataLayer || [];
function gtag() { dataLayer.push(arguments); }

const googleLoadPlatformScript = (url) => {
    const { gapi } = window;

    if (gapi) {
        return new Promise(resolve => resolve());
    }

    return loadScript(url)
};

const googleLoadRatingBadge = merchantId => {
    setTimeout(() => {
        googleLoadPlatformScript('https://apis.google.com/js/platform.js')
            .then(() => {
                const ratingBadgeContainer = document.createElement('div');
                document.body.appendChild(ratingBadgeContainer);
                window.gapi.load('ratingbadge', () => {
                    window.gapi.ratingbadge.render(ratingBadgeContainer, { merchant_id: merchantId });
                });
            }).catch(() => console.log('load google platform script error'))
    }, 5000);
};

const parseCookies = () => {
    const cookies = JSON.parse(getCookie(COOKIE_CONSENT) || '{}');
    const result = {};
    const cookiesOptions = {
        '1': ['security_storage', 'functionality_storage'],
        '2': ['analytics_storage'],
        '3': ['ad_storage', 'ad_user_data', 'ad_personalization'],
        '4': ['personalization_storage'],
    };

    Object.keys(cookies).forEach(key => {
        cookiesOptions[key]?.forEach(option => {
            result[option] = !!cookies[key] ? GRANTED : DENIED;
        });
    });

    return result;
};

const googleCookiesConsentUpdate = () => {
    gtag('consent', 'update', parseCookies());
};

const gtmScript = id => {
    const script = document.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;

    document.head.appendChild(script);
};

const gtmNoScript = id => {
    const script = document.createElement('noscript');
    const iframe = document.createElement('iframe');

    iframe.setAttribute('src', `https://www.googletagmanager.com/ns.html?id=${id}`);
    iframe.setAttribute('width', '0');
    iframe.setAttribute('height', '0');
    iframe.setAttribute('style', 'display:none;visibility:hidden');

    script.appendChild(iframe);

    document.body.prepend(script);
};

const googleLoadTagManager = id => {
    setTimeout(() => {
        gtmScript(id);
        gtmNoScript(id);
    }, 5000);
};

const googleAddToCart = ({
    cartItems,
    addedItems,
    cart: {
        prices: {
            grand_total = {}
        } = {}
    } = {},
}) => {
    let ecommerceCurrency = '';
    let ecommercePrice = 0;
    const itemsList = [];

    addedItems.forEach(({ sku: addedSku, quantity }) => {
        const { product: { sku, name, price_range } } = cartItems.find(({ product: { sku } }) => sku === addedSku);

        itemsList.push({ sku, name, price_range, quantity });
    });

    const items = itemsList.map(({
        sku,
        name,
        price_range: {
            minimum_price: {
                regular_price: { value, currency },
                final_price,
            }
        },
        quantity,
    }) => {
        ecommerceCurrency = currency;
        ecommercePrice = ecommercePrice + value * quantity;

        return {
            item_id: sku,
            item_name: name,
            price: value,
            quantity: quantity,
            special_price: final_price.value,
        }
    });

    const ecommerce = {
        currency: ecommerceCurrency,
        value: ecommercePrice,
        items,
        grand_total: grand_total.value
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'add_to_cart', ecommerce });
};

const googleRemoveFromCart = ({
    sku,
    name,
    price_range: {
        minimum_price: {
            regular_price: { value, currency }
        }
    },
    quantity,
}) => {
    const ecommerce = {
        value: value * quantity,
        currency: currency,
        items: [
            {
                item_id: sku,
                item_name: name,
                price: value,
                quantity: quantity
            }
        ]
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'remove_from_cart', ecommerce });
};

const googleViewItem = ({
    sku,
    name,
    price_range: {
        minimum_price: {
            regular_price: { value, currency }
        }
    }
}) => {
    const ecommerce = {
        items: [
            {
                item_id: sku,
                item_name: name,
                currency: currency,
                price: value,
            }
        ]
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'view_item', ecommerce });
};

const googleViewItemList = ({ sku, name, variants }) => {
    let ecommerceCurrency = '';

    const items = variants.map(({
        product: {
            sku,
            name,
            price_range: {
                minimum_price: {
                    regular_price: { value, currency }
                }
            }
        }
    }) => {
        ecommerceCurrency = currency;

        return {
            item_id: sku,
            item_name: name,
            price: value,
        }
    });

    const ecommerce = {
        item_list_id: sku,
        item_list_name: name,
        currency: ecommerceCurrency,
        items,
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'view_item_list', ecommerce });
};

const googleSelectItem = ({
    sku,
    name,
    variantSKU,
    variantName,
    price_range: {
        minimum_price: {
            regular_price: { value }
        }
    }
}) => {
    const ecommerce = {
        item_list_id: sku,
        item_list_name: name,
        items: [{
            item_id: variantSKU,
            item_name: variantName,
            price: value,
            item_list_id: sku,
            item_list_name: name,
        }]
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'select_item', ecommerce });
};

const googleBeginCheckout = ({
    items,
    prices: {
        discounts = [],
        grand_total = {},
    } = {},
}) => {
    const ecommerce = {
        value: grand_total.value,
        currency: grand_total.currency,
        coupon: discounts?.map(({ label }) => label).join(' ') || '',
        items: items.map(({
            product: {
                sku,
                name,
            },
            prices: {
                row_total: { value }
            },
            quantity,
        }) => ({
            item_id: sku,
            item_name: name,
            price: value,
            quantity: quantity
        }))
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'begin_checkout', ecommerce });
};

const googlePurchase = ({
    number,
    items: orderItems,
    total: {
        discounts,
        total_shipping,
        grand_total,
        total_tax,
    },
    enhanced_conversion_data: {
        email,
        firstname,
        lastname,
        street,
        city,
        region,
        postcode,
        country,
        telephone,
        revenue,
        order_number,
        item_count,
        coupon,
        customer_type,
        payment_method,
        shipping,
        discount,
        numeber_of_lines,
    },
}) => {
    const coupons = discounts?.map(({ label }) => label).join(' ') || '';

    const items = orderItems.map(({
        product: {
            sku,
            name,
        },
        product_sale_price: {
            value,
        },
        quantity_ordered
    }) => ({
        item_id: sku,
        item_name: name,
        price: value,
        quantity: quantity_ordered,
    }));

    const ecommerce = {
        transaction_id: number,
        value: grand_total.value,
        tax: total_tax.value,
        shipping: total_shipping.value,
        currency: grand_total.currency,
        coupon: coupons,
        items,
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: 'purchase',
        ecommerce,
        order_value: grand_total.value,
        order_id: number,
        enhanced_conversion_data: {
            email,
            first_name: firstname,
            last_name: lastname,
            street,
            city,
            region,
            postal_code: postcode,
            country,
            phone_number: telephone,
            revenue,
            order_number,
            item_count,
            coupon,
            customer_type,
            payment_method,
            shipping,
            discount,
            numeber_of_lines,
            product_names: orderItems.map(({ product: { name } }) => name).join(', '),
        },
    });
};

const googlePageView = title => {
    function getReferrer() {
        const referrer = sessionStorage.getItem('customReferrer') ? sessionStorage.getItem('customReferrer') : document.referrer;

        sessionStorage.setItem('customReferrer', window.location.href);

        return referrer;
    }

    dataLayer.push({
        'event': 'page_view',
        'virtual_page_title': title,
        'virtual_page_location': window.location.href,
        'virtual_page_referrer': getReferrer(),
    });
};

const googleTrackingLoginEvent = ({ event, loginEvent, method }) => {
    window.dataLayer.push({
        event: event,
        login_method: loginEvent,
        method: method,
    });
};

export {
    googleLoadRatingBadge,
    googleLoadTagManager,
    googleAddToCart,
    googleRemoveFromCart,
    googleViewItem,
    googleViewItemList,
    googlePurchase,
    googleCookiesConsentUpdate,
    googleSelectItem,
    googleBeginCheckout,
    googlePageView,
    googleTrackingLoginEvent,
};
